/**
 * S49.6 — silent renewal, single-flight.
 *
 * ⚠️ THE REFRESH TOKEN WAS NOT ONLY UNREDEEMABLE, IT WAS MOSTLY NOT KEPT. Before this,
 * `refresh_token` appeared in three login paths as a typed field that nothing ever stored:
 * password login (useAuth.ts), session select/switch (SessionSelect.tsx) and the session
 * establish path all destructured it and dropped it on the floor. Only `OIDCCallback.tsx:180`
 * wrote it anywhere. So even once POST /auth/refresh existed (S49.5), most sessions had no
 * credential to present. This module is the one place that keeps it and the one place that
 * spends it.
 *
 * ⚠️ SINGLE-FLIGHT IS A CORRECTNESS REQUIREMENT, NOT AN OPTIMISATION. The server rotates the
 * refresh token on every redemption and treats a spent token as theft, revoking the whole
 * session family. A dashboard fires many requests at once; when the access token expires they
 * all 401 within milliseconds. Without single-flight, request #2 presents the token request #1
 * already rotated away, the server correctly reads that as reuse, and the user is logged out.
 * The bug would present as "renewal signs me out, but only sometimes, and only under load" --
 * which is close to undiagnosable from a report. One in-flight promise; everyone awaits it.
 *
 * ⚠️ WHY sessionStorage AND NOT localStorage. The access token already lives in localStorage
 * (zustand persist), so a new tab has an access token but will have no refresh token, and when
 * that tab's access token expires it will require a fresh sign-in rather than renewing. That is
 * a deliberate trade: putting a 24-hour credential in localStorage would meaningfully raise
 * what a single XSS is worth, whereas the new-tab case degrades to exactly what happens to
 * EVERY tab today -- signing in again after 15 minutes. Strictly better than the status quo,
 * with no new exposure. S49.11 moves both to an httpOnly cookie and removes the trade entirely.
 */

import { useAuthStore } from '../stores/authStore';
import { setAuthToken } from '../api-client';

const REFRESH_KEY = 'refresh_token'; // key already used by OIDCCallback.tsx

export function storeRefreshToken(token: string | null | undefined): void {
  try {
    if (token) sessionStorage.setItem(REFRESH_KEY, token);
    else sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    /* storage unavailable: renewal degrades to re-login, which is today's behaviour */
  }
}

export function getRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

/** The single in-flight renewal. Null when none is running. */
let inFlight: Promise<string | null> | null = null;

/** Test seam: drop any in-flight promise between cases. */
export function __resetRefreshState(): void {
  inFlight = null;
}

async function performRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  let response: Response;
  try {
    // ⚠️ Plain fetch, NOT apiFetch. apiFetch dispatches auth:unauthorized on a 401, and a
    // failed renewal returning 401 would then trigger the very handler that asks for a
    // renewal -- an unbounded loop that ends the session anyway, but noisily.
    response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // ⚠️ A NETWORK failure is not an expired session. Returning null here would sign out
    // everyone who briefly lost wifi. Signal "unknown" by leaving the tokens intact; the
    // caller retries on the next tick.
    return null;
  }

  if (!response.ok) {
    // A refused refresh IS terminal: the token is spent, revoked, or the session is gone.
    storeRefreshToken(null);
    return null;
  }

  const data = (await response.json()) as { access_token?: string; refresh_token?: string };
  if (!data.access_token) return null;

  storeRefreshToken(data.refresh_token);
  setAuthToken(data.access_token);
  useAuthStore.getState().setToken(data.access_token);
  return data.access_token;
}

/**
 * Renew the access token. Concurrent callers share one request and one result.
 *
 * Resolves to the new access token, or null when the session could not be renewed.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight) return inFlight;
  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
